import * as React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  footer?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  overlayClassName?: string;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-4xl',
  full: 'max-w-full',
};

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  footer,
  className = '',
  headerClassName = '',
  bodyClassName,
  footerClassName,
  overlayClassName = '',
}: ModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  const modalContent = (
    <div
      className={cn(
        'fixed inset-0 z-[5000] flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200 backdrop-blur-[1px]',
        overlayClassName,
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={cn(
          'flex min-h-0 max-h-[92vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.15)] animate-in fade-in zoom-in-95 duration-200',
          SIZE_CLASSES[size],
          className,
        )}
      >
        {(title || showCloseButton) && (
          <div className={cn('flex items-start justify-between bg-white px-6 py-4', headerClassName)}>
            {title && (
              <div className="min-w-0 flex-1 pr-4">
                {typeof title === 'string' ? (
                  <>
                    <h3 className="font-semibold text-base leading-snug text-slate-900">{title}</h3>
                    {description ? (
                      <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{description}</p>
                    ) : null}
                  </>
                ) : (
                  title
                )}
              </div>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="-mt-0.5 flex-shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-primary/5 hover:text-primary"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

        <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-4', bodyClassName)}>{children}</div>

        {footer ? (
          <div className={cn('flex-shrink-0 border-t border-slate-100 bg-white px-6 py-4', footerClassName)}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
